/**
 * CascadePlanPreviewCard — inline preview of a cascade plan stored on a
 * Change Request payload (#262, 2026-06-08 design contract).
 *
 * Used by ChangeRequestDetailPage to make the cascade visible to the
 * approver. The full footprint with entity labels is NOT in the plan —
 * the plan stores just the id sets. The approver sees summary counts;
 * any drift from current state is caught by the apply-time mismatch
 * check (HTTP 409 with re-confirmation).
 *
 * Distinct from CascadeStatusModal, which fetches the live footprint
 * (with labels) for the user filing the change. The CR detail page
 * needs to show what was FILED, not what's current — so it uses the
 * plan dict directly.
 */
import { Alert, Badge, Card, Group, Stack, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';

import type { CascadePlan } from '../api/agreements';

export interface CascadePlanPreviewCardProps {
  plan: CascadePlan;
}

export function CascadePlanPreviewCard({ plan }: CascadePlanPreviewCardProps) {
  const isClosed = plan.to_status === 'Closed';
  const total =
    plan.summary.direct_accounts
    + plan.summary.policies
    + plan.summary.indirect_accounts;

  return (
    <Card withBorder p="md" radius="sm">
      <Stack gap="sm">
        <Text fw={600} size="sm">
          Cascade plan
        </Text>
        <Alert
          icon={<IconAlertTriangle size={18} />}
          color={isClosed ? 'red' : 'orange'}
          variant="light"
        >
          {isClosed
            ? (
              <>
                <strong>Permanent action.</strong> Approving will mark this
                Agreement <strong>Closed</strong> and cascade <strong>Closed</strong>{' '}
                to <strong>{total}</strong> descendant
                record{total === 1 ? '' : 's'}. Policies will be detached.
                Closed entities cannot be re-activated.
              </>
            )
            : (
              <>
                Approving will mark this Agreement{' '}
                <strong>{plan.to_status}</strong> and cascade{' '}
                <strong>{plan.to_status}</strong> to <strong>{total}</strong>{' '}
                descendant record{total === 1 ? '' : 's'}. Policies will be
                detached from this Agreement.
              </>
            )}
        </Alert>
        <Group gap="xl">
          <Stack gap={0}>
            <Text size="xs" c="dimmed" tt="uppercase">Direct Accounts</Text>
            <Badge size="lg" variant="light" color={plan.summary.direct_accounts > 0 ? 'orange' : 'gray'}>
              {plan.summary.direct_accounts}
            </Badge>
          </Stack>
          <Stack gap={0}>
            <Text size="xs" c="dimmed" tt="uppercase">Policies (detached)</Text>
            <Badge size="lg" variant="light" color={plan.summary.policies > 0 ? 'orange' : 'gray'}>
              {plan.summary.policies}
            </Badge>
          </Stack>
          <Stack gap={0}>
            <Text size="xs" c="dimmed" tt="uppercase">Indirect Accounts</Text>
            <Badge size="lg" variant="light" color={plan.summary.indirect_accounts > 0 ? 'orange' : 'gray'}>
              {plan.summary.indirect_accounts}
            </Badge>
          </Stack>
        </Group>
        <Text size="xs" c="dimmed">
          Plan captured at filing time. If descendants changed since then,
          the apply step will surface a mismatch and require re-confirmation.
        </Text>
      </Stack>
    </Card>
  );
}
